import { useAuth } from "@/contexts/AuthContext";
import { useDialog } from "@/contexts/DialogContext";
import { NAV_BUTTON_PRIMARY_CLASS } from "@/components/navStyles";
import { Button } from "@/components/ui/button";
import { Role, isInternalRole } from "@/hooks/useRbac";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@openuidev/react-ui/Select";
import { useInvitedUsers } from "@/hooks/useUserManagement";
import { forwardRef, useState } from "react";
import { showToast } from "@/helpers/toast.helpers";
import { CircleCheck, CircleX } from "lucide-react";
import { TagInput } from "../ui/tagInput";
import { cn, normalizeEmail } from "@/lib/utils";
import React from "react";
import { toastStyle } from "@/config/theme";
import { getErrorObject } from "@/helpers/http.helpers";
import { MESSAGES } from "@/constants";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { POSTHOG } from "@/constants";
const emailValidator = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
export const InviteUser = () => {
    const { roles, userDetails } = useAuth();
    // `roles` comes from the RBAC roles query (useRbac → useQuery(["roles"])); it is
    // `undefined` while loading and on a failed fetch (e.g. the user-service 404 under
    // `pnpm local`). Never map/index it directly — degrade gracefully instead.
    // Saras-staff personas are dropped: `/role/all` now returns the FULL catalog
    // (`internal=true`) so capability resolution works for internal users, but a
    // customer must never be able to invite someone INTO an internal role.
    const availableRoles: Role[] = ((roles ?? []) as Role[]).filter((role) => !isInternalRole(role));
    const rolesUnavailable = availableRoles.length === 0;
    const { closeDialog } = useDialog();
    const { inviteUser, isInviting } = useInvitedUsers({ companyId: userDetails?.company?.companyId, productType: 'IQ' });
    const { trackEvent } = usePostHogTracking();
    const [roleId, setRoleId] = useState<string | null>(null);

    const [emailTags, setEmailTags] = useState<string[]>([]);

    const handleSubmit = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!emailTags || !roleId || !userDetails?.company?.companyId) return;

        const payload = emailTags.map((email)=>{
          return {
            firstName: '',
            lastName: '',
            email: normalizeEmail(email),
            roleIds: [parseInt(roleId, 10)],
            productType: "IQ",
          }
        });

        inviteUser({ companyId: userDetails.company.companyId, payload }, {
          onSuccess,
          onError
        });
    };

    const onSuccess = (response: unknown) => {
      closeDialog();
      const result = response as { errors?: Array<{ message?: string }>; data?: unknown[] };
      const error = result?.errors?.[0];
      const successUser = result?.data?.[0];

      setTimeout(() => {
        if (error) {
          showToast(<InviteUserToast type="Error" message={error.message || MESSAGES.ERRORS.INVITE_USER} actionLabel="Resend" onActionClick={() => handleSubmit()} />, { position: 'bottom-right', style: toastStyle, classNames: { content: 'w-full' }});
        } else if (successUser) {
          showToast(<InviteUserToast type="Success" message={MESSAGES.SUCCESS.INVITE_USER} />, { position: 'bottom-right', style: toastStyle, classNames: { content: 'w-full' }});
          trackEvent(POSTHOG.EVENTS.USER_INVITED,{
            companyId:userDetails?.company?.companyId
          })
        }
      }, 300);
    }
    const onError = (error: Error) => {
      const errorObject = getErrorObject(error);
      /* TO-DO: handle by user codes
      if(errorObject.code === errorCodes.USER_EXISTS){
        setInputError({ message: errorObject.message, code: errorObject.code});
        return;
      }
      */
      closeDialog();
      setTimeout(() => {
        showToast(<InviteUserToast type="Error" message={errorObject.message || MESSAGES.ERRORS.INVITE_USER} actionLabel="Resend" onActionClick={() => handleSubmit()} />, { position: 'bottom-right', style: toastStyle, classNames: { content: 'w-full' }});
      }, 300);
    }

    return (
      <div className="mt-1 flex w-full min-w-0 flex-col gap-2">
        <p className="mb-2 text-sm text-[color:var(--dash-muted,var(--text-medium-emphasis))]">
          Invited users will receive an email to accept their invite
        </p>
        <form className="flex w-full min-w-0 flex-col gap-6" onSubmit={handleSubmit}>
          <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:items-start">
            <TagInput
              className="min-h-[60px] min-w-0 flex-1 sm:basis-0"
              tags={[]}
              setTags={(tags: string[]) => {
                setEmailTags(tags);
              }}
              validators={[
                {
                  validatorFn: emailValidator,
                  errorMessage: "Please enter a valid email",
                },
              ]}
              placeholder="Email ID"
            />
            <div className="min-w-0 flex-1 sm:basis-0">
              <Select
                onValueChange={setRoleId}
                disabled={isInviting || rolesUnavailable}
              >
                <SelectTrigger
                  size="lg"
                  className="h-10 w-full min-w-0 border border-[var(--dash-hairline,var(--grey-200))]"
                  style={{
                    backgroundColor:
                      "var(--dash-surface,var(--structural-primary-tint))",
                  }}
                >
                  <SelectValue
                    placeholder={
                      rolesUnavailable ? "Roles unavailable" : "Role"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role: Role) => (
                    <SelectItem
                      key={role.roleId}
                      value={role.roleId.toString()}
                    >
                      {role.roleName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-4">
            <Button
              type="button"
              className="cursor-pointer"
              variant="outline"
              onClick={() => closeDialog()}
              disabled={isInviting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className={cn(
                NAV_BUTTON_PRIMARY_CLASS,
                "h-10 w-full min-w-0 px-5 sm:w-auto sm:min-w-[9.5rem]",
              )}
              disabled={
                isInviting || rolesUnavailable || !emailTags.length || !roleId
              }
            >
              Send Invite(s)
            </Button>
          </div>
        </form>
      </div>
    );

  }

interface InviteUserToastProps {
  type: 'Success' | 'Error';
  message: string;
  actionLabel?: string;
  onActionClick?: () => void;
}

export const InviteUserToast = forwardRef<HTMLDivElement, InviteUserToastProps>(
  ({ type, message, actionLabel, onActionClick }, ref) => {
    return (
      <div ref={ref} className="w-full">
        <div className="flex items-center gap-3">
          {type === 'Success' && <CircleCheck className="h-5 w-5 min-w-5 text-[color:var(--dash-pos,var(--semantic-success-500))]" />}
          {type === 'Error' && <CircleX className="h-5 w-5 min-w-5 text-[color:var(--dash-neg,var(--semantic-error-500))]" />}
          <p className="text-sm font-medium text-foreground">{message}</p>
        </div>
        <div className="flex justify-end">
          {type === 'Error' && actionLabel && onActionClick && (
            <Button variant="ghost" size="sm" onClick={onActionClick}>
              {actionLabel}
            </Button>
          )}
        </div>
      </div>
    );
  }
);

InviteUserToast.displayName = "InviteUserToast";